/**
 * @fileoverview The typed command line of the PyMOL backend ("viewer commands"): BMaps' policy
 * on top of the wrapper's restricted mode, execution command by command, completion, and the
 * "user overlay" bookkeeping behind "Reapply BMaps display".
 *
 * Plan section 2.5 (conifer-workspace docs/pymol-wasm-visualizer-plan.md); NOTES.md
 * "Command input" has the policy table as implemented.
 *
 * Two layers, deliberately:
 *   1. The wrapper's restricted mode (pymol-web python/bridge.py) is the SECURITY boundary:
 *      PyMOL's own `secure=1` parsing plus the bridge's deny-list refuse Python, expressions
 *      (alter / iterate / label ...), scripts and file / network access. It runs on every
 *      command we send, whatever this file decides.
 *   2. This file adds BMaps' CONSISTENCY rules: BMaps owns the molecules it loaded (objects
 *      named `_bm_...`, see PyMOLConstants.ObjectPrefix) and keeps a shadow model of them, so
 *      commands that would restructure, move, rename or delete those objects are refused,
 *      appearance changes on them are allowed as a "user overlay", and objects the user made
 *      are the user's.
 *   Layer 2 is not a security boundary: it protects BMaps' model from accidents, not from a
 *   determined user, who can at most break their own session.
 *
 * Command text is only ever executed when the user typed or pasted it: nothing here stores
 * or replays commands (O7), and the only mode used is 'restricted' (O6, see
 * UnrestrictedModeEnabled below).
 */

import { EventBroker } from '../eventbroker';
import { NativeCommandStateEvent } from '../VisualizerInterface';
import { ObjectPrefix, SettingDefaults, RayMaxDimension } from './PyMOLConstants';
import { SceneSettings } from './PyMOLScene';

/**
 * How the feature is named to users. The feature must never be called "PyMOL" on its own
 * (trademark; plan section 1.2). The PyMOL licence's trademark notice permits four slogans -
 * "Includes PyMOL(TM).", "Based on PyMOL(TM) technology.", "Contains PyMOL(TM) source code.",
 * "Built using PyMOL(TM)." - provided the notice below is included too (owner's choice,
 * 2026-09-26: "Includes PyMOL™").
 */
export const Attribution = 'Includes PyMOL™';

/** The licence's required footnote to the slogan, verbatim. */
export const TrademarkNotice = 'PyMOL is a trademark of Schrodinger, LLC.';

/**
 * O6 (owner decision, plan section 4): an "advanced / unrestricted" preference would run typed
 * text with the wrapper's `mode: 'full'` - Python, alter / iterate / label expressions, scripts,
 * files - which under Pyodide is arbitrary JavaScript in the BMaps origin with the user's
 * session (`import js`). NOT ENABLED. Nothing calls `setUnrestrictedRequested()`; if the owner
 * decides for it, a preference would call `setUnrestrictedRequested(true)` AND this constant
 * would have to be flipped - the managed-object policy below would still apply.
 */
export const UnrestrictedModeEnabled = false;

/** Refused command classes, as shown under "What is refused?" in the command panel. */
export const RestrictionSummary = [
    'Python code, and commands that evaluate Python expressions (alter, iterate, label, system, alias, set_key ...).',
    'Files, scripts and the network (load, fetch, save, png, run, @script, cd, ls ...): use BMaps\' own import and export.',
    'Changes to BMaps\' own molecules: delete, remove, create / extract / copy from them, h_add, bond / unbond, translate / rotate, sculpting, fuse, editing, alignment that moves them, renaming. The same commands are allowed on objects you create (pseudoatom, select, distance ...).',
    'Mouse and editing modes, undo / redo, wizards, the window size, reinitialize / quit, and global settings BMaps relies on (e.g. transparency_picking_mode, stick_transparency, auto_*).',
    'Appearance commands (show, hide, color, set with a selection, cartoon ...) on BMaps\' molecules are allowed: they stay until BMaps redraws those molecules, or until you press "Reapply BMaps display".',
    'Typed commands are not saved with BMaps sessions.',
];

// ----------------------------------------------------------------------------------------------
// Parsing: ports of PyMOL's own routines (modules/pymol/parsing.py, 3.2), so that the text we
// check is the text PyMOL will run. Nothing here evaluates anything.
// ----------------------------------------------------------------------------------------------

const Pairs = {
    '(': ')', '[': ']', '{': '}', "'": "'", '"': '"',
};

/**
 * `parsing.split(line, ';')`: split a command line into commands, keeping quoted strings and
 * bracketed groups (selections) intact. Empty commands are dropped, as PyMOL's parser does.
 * @param {string} line
 * @returns {string[]}
 */
export function splitCommands(line) {
    const s = String(line);
    const out = [];
    let stack = [];
    let word = '';
    let c = 0;
    while (c < s.length && s[c] === ';') c++;
    for (; c < s.length; c++) {
        const ch = s[c];
        if (ch === ';' && stack.length === 0) {
            out.push(word.trim());
            word = '';
            continue;
        }
        if (stack.length > 0) {
            if (ch === stack[0]) stack = stack.slice(1);
            else if (Pairs[ch]) stack = [Pairs[ch], ...stack];
        } else if (Pairs[ch]) {
            stack = [Pairs[ch], ...stack];
        }
        word += ch;
    }
    if (word.length > 0) out.push(word.trim());
    return out.filter((cmd) => cmd.length > 0);
}

/** Index where the last command of a line starts (after the last top-level ';'). */
function lastCommandStart(line) {
    let stack = [];
    let start = 0;
    for (let c = 0; c < line.length; c++) {
        const ch = line[c];
        if (ch === ';' && stack.length === 0) {
            start = c + 1;
        } else if (stack.length > 0) {
            if (ch === stack[0]) stack = stack.slice(1);
            else if (Pairs[ch]) stack = [Pairs[ch], ...stack];
        } else if (Pairs[ch]) {
            stack = [Pairs[ch], ...stack];
        }
    }
    return start;
}

const ArgNameRe = /^[A-Za-z0-9_]+\s*=/;
const NesterRe = /^[^,;]*[([]/;
const ArgPreNesterRe = /^([^,;([]+)[([]/;
const ArgPostNesterRe = /^[^,;([]*/;
const ArgEasyNesterRe = /^(?:\([^,]*\)|\[[^,]*\])/;
const ArgHardNesterRe = /^(?:\(.*\)|\[.*\])/;
const NesterCharRe = /[()[\]]/g;
const ArgValueRe = /^(?:'''[^']*'''|'[^']*'|"[^"]*"|[^,;]+)/;

/** `parsing.trim_nester`: the first balanced (...) or [...] group, or null. */
function trimNester(st) {
    let depth = 1;
    let c = 1;
    while (c < st.length) {
        if (st[c] === '(' || st[c] === '[') depth++;
        if (st[c] === ')' || st[c] === ']') depth--;
        c++;
        if (!depth) break;
    }
    return depth ? null : st.slice(0, c);
}

/**
 * `parsing.parse_arg` for the STRICT / LEGACY / NO_CHECK modes: the arguments of ONE command.
 * @param {string} command the whole command, keyword included
 * @returns {Array<[string|null, string|null]>} (name, value) pairs; throws on a syntax error
 */
export function parseArgs(command) {
    const result = [];
    const m = /^\s*\S+\s+([\s\S]*)$/.exec(String(command).replace(/\n/g, ''));
    if (!m) return result;
    let st = m[1];
    let cc = 0;
    for (;;) {
        st = st.replace(/^\s+/, '');
        if (st === '') break;
        let name = null;
        const named = ArgNameRe.exec(st);
        if (named) {
            name = named[0].slice(0, -1).trim();
            st = st.slice(named[0].length).replace(/^\s+/, '');
        }
        let skip = false;
        if (NesterRe.test(st.slice(cc))) {
            skip = true;
            let nested = '';
            let again = true;
            while (again) {
                again = false;
                const pre = ArgPreNesterRe.exec(st.slice(cc));
                if (pre) {
                    nested += pre[1];
                    cc += pre[1].length;
                }
                let easy = ArgEasyNesterRe.exec(st.slice(cc));
                if (easy && (easy[0].match(NesterCharRe) || []).length % 2 === 1) easy = null;
                if (easy) {
                    nested += easy[0];
                    cc += easy[0].length;
                    const post = ArgPostNesterRe.exec(st.slice(cc));
                    nested += post[0];
                    cc += post[0].length;
                    again = true;
                } else {
                    const hard = ArgHardNesterRe.exec(st.slice(cc));
                    if (hard) {
                        const group = trimNester(hard[0]);
                        if (group === null) throw new Error('syntax error (unbalanced brackets)');
                        cc += group.length;
                        nested += group;
                        const post = ArgPostNesterRe.exec(st.slice(cc));
                        nested += post[0];
                        cc += post[0].length;
                        again = true;
                    }
                }
            }
            if (nested.length === 0) skip = false;
            else result.push([name, nested.trim()]);
        }
        if (!skip) {
            let value = ArgValueRe.exec(st.slice(cc));
            if (!value) {
                if (st.slice(cc, cc + 1) !== ',') throw new Error('syntax error');
                result.push([name, null]);
            } else {
                let text = value[0];
                cc += value[0].length;
                for (;;) {
                    value = ArgValueRe.exec(st.slice(cc));
                    if (!value) break;
                    text += value[0];
                    cc += value[0].length;
                }
                result.push([name, text.trim()]);
            }
        }
        st = st.slice(cc).replace(/^\s+/, '');
        cc = 0;
        if (st !== '') {
            if (st.startsWith(',')) st = st.slice(1).replace(/^\s+/, '');
            else throw new Error('syntax error');
        }
    }
    return result;
}

/**
 * `parsing.prepare_call`, reduced to what the policy needs: map (name, value) pairs onto the
 * command's parameter names. LEGACY mode turns `set foo=1` into two positional arguments when
 * `foo` is not a parameter name.
 * @param {string[]} params positional parameter names
 * @param {Array<[string|null, string|null]>} parsed
 * @param {{legacy?: boolean}} [options]
 * @returns {{args: Object<string, string|null>, extra: Array<string|null>}}
 */
export function bindArgs(params, parsed, { legacy=false }={}) {
    let list = parsed;
    if (legacy) {
        list = [];
        for (const [name, value] of parsed) {
            if (name !== null && !params.includes(name)) list.push([null, name], [null, value]);
            else list.push([name, value]);
        }
    }
    const args = {};
    const extra = [];
    list.forEach(([name, value], i) => {
        if (name === null) {
            if (i < params.length) args[params[i]] = value;
            else extra.push(value);
        } else {
            args[name] = value;
        }
    });
    return { args, extra };
}

function stripQuotes(text) {
    return String(text).trim().replace(/^(['"])(.*)\1$/, '$2');
}

// ----------------------------------------------------------------------------------------------
// Names
// ----------------------------------------------------------------------------------------------

/**
 * The name PyMOL will actually use for `name`: ObjectMakeValidName (layer1/PyMOLObject.cpp)
 * keeps `A-Z a-z 0-9 + - . ^ _`, turns any other run of characters into one `_` and drops such
 * runs at either end. So `~_bm_cmp3`, `"_bm_cmp3 "` and `é_bm_cmp3` all name `_bm_cmp3`.
 * @param {string} name
 * @returns {string}
 */
export function pymolValidName(name) {
    // after the first step a space is the only character outside the legal set
    return String(name)
        .replace(/[^A-Za-z0-9+\-.^_]+/g, ' ')
        .trim()
        .replace(/ /g, '_');
}

/**
 * Is this a BMaps-managed (reserved) name? Case-insensitive, like PyMOL's name matching, and
 * judged on the name PyMOL would give it, so no decoration gets a new name past the prefix
 * check: `set_name mine, ~_bm_cmp3` renames onto - and so deletes - BMaps' `_bm_cmp3`.
 */
export function isManagedName(name) {
    if (typeof name !== 'string') return false;
    const asPyMOL = pymolValidName(stripQuotes(name)).toLowerCase();
    return asPyMOL.startsWith(ObjectPrefix.toLowerCase());
}

function wildcardRegExp(token) {
    const body = token.split('').map((ch) => {
        if (ch === '*') return '.*';
        if (ch === '?') return '.';
        return ch.replace(/[.+^${}()|[\]\\]/g, '\\$&');
    }).join('');
    return new RegExp(`^${body}$`, 'i');
}

/**
 * Which of `names` a PyMOL object-name pattern (`delete`, `enable`, `set_name` ...) can reach.
 * Mirrors ExecutiveGetNamesListFromPattern CONSERVATIVELY: a leading `not ` / `!` negates;
 * `all`, `*` and `enabled` mean everything; tokens are split on white space, `+` and `,`;
 * `*` and `?` are wildcards; matching ignores case; and a token also matches every name it
 * is a prefix of (PyMOL resolves unambiguous prefixes for some commands). Over-matching only
 * ever makes the policy stricter.
 * @param {string} pattern
 * @param {string[]} names
 * @returns {string[]}
 */
export function namesMatching(pattern, names) {
    let text = stripQuotes(pattern == null ? '' : pattern);
    let negate = false;
    if (/^not\s/i.test(text)) {
        negate = true;
        text = text.slice(4);
    } else if (text.startsWith('!')) {
        negate = true;
        text = text.slice(1);
    }
    text = text.trim();
    const matched = new Set();
    const tokens = text.split(/[\s+,]+/).map((t) => stripQuotes(t).replace(/^[%?]+/, '')).filter(Boolean);
    for (const token of tokens) {
        const lower = token.toLowerCase();
        if (lower === 'all' || lower === 'enabled' || /^\*+$/.test(token)) {
            names.forEach((n) => matched.add(n));
            continue;
        }
        const re = wildcardRegExp(token);
        for (const name of names) {
            if (re.test(name) || name.toLowerCase().startsWith(lower)) matched.add(name);
        }
    }
    return negate ? names.filter((n) => !matched.has(n)) : [...matched];
}

/**
 * Every string PyMOL's `Shortcut` could resolve to `name`: its prefixes, and the prefixes of
 * the underscore abbreviations (`st_transparency`, `s_t` ... for `stick_transparency`).
 */
function shortcutForms(name) {
    const forms = new Set();
    for (let i = 1; i <= name.length; i++) forms.add(name.slice(0, i));
    if (name.includes('_')) {
        for (const n of [1, 2]) {
            const groups = name.split('_');
            const abbreviation = [...groups.slice(0, -1).map((g) => g.slice(0, n)), groups[groups.length - 1]].join('_');
            for (let i = abbreviation.indexOf('_') + 1; i <= abbreviation.length; i++) {
                forms.add(abbreviation.slice(0, i));
            }
        }
    }
    return forms;
}

/**
 * Global settings BMaps relies on: the scene settings it applies, the per-atom style settings
 * whose global default the renderer assumes (PyMOLConstants.SettingDefaults), the wrapper's
 * embed invariants, name-matching behaviour this file depends on, and settings that change
 * how BMaps' next CIF load is read. Setting them for a selection or object is fine.
 */
const ProtectedSettings = [
    ...Object.keys(SceneSettings), ...Object.keys(SettingDefaults),
    'auto_zoom', 'internal_feedback', 'internal_gui', 'internal_gui_width', 'max_threads',
    'use_shaders', 'show_progress', 'wildcard', 'ignore_case', 'ignore_case_chem',
    'raise_exceptions', 'suspend_updates', 'security', 'retain_order',
];
const ProtectedSettingPrefixes = ['auto_', 'cif_', 'connect_'];
const ProtectedSettingForms = new Set(ProtectedSettings.flatMap((s) => [...shortcutForms(s)]));

/** Could this (possibly abbreviated) setting name mean a protected setting? Conservative. */
export function isProtectedSetting(name) {
    const text = stripQuotes(name == null ? '' : name).toLowerCase();
    if (!text) return false;
    if (/^\d+$/.test(text)) return true; // settings can be given by index
    if (ProtectedSettingForms.has(text)) return true;
    return ProtectedSettingPrefixes.some((p) => text.startsWith(p) || p.startsWith(text));
}

// ----------------------------------------------------------------------------------------------
// Output
// ----------------------------------------------------------------------------------------------

/**
 * Kind of one line of PyMOL output, for styling.
 * @param {string} text
 * @returns {'error'|'warning'|'output'}
 */
export function classifyOutputLine(text) {
    const s = String(text);
    if (/^\s*([\w.]+-)?Error\b/i.test(s) || /^\s*Traceback\b/.test(s)
        || /^\s*[A-Za-z_][\w.]*(Error|Exception)\b/.test(s)) return 'error';
    if (/^\s*([\w.]+-)?Warning\b/i.test(s)) return 'warning';
    return 'output';
}

// ----------------------------------------------------------------------------------------------
// The policy table
// ----------------------------------------------------------------------------------------------

/**
 * Policy classes. `view` and `user` never touch BMaps' molecules; `appearance` may, and marks
 * the objects it touched as overlaid; `structural` is refused when it would touch them;
 * `deny` and `io` are always refused.
 */
export const PolicyClass = {
    view: 'view',
    appearance: 'appearance',
    user: 'user',
    structural: 'structural',
    deny: 'deny',
    io: 'io',
};

/** Target default meaning "the editing picks pk1..pk4 that exist" (edit-mode commands). */
const PICKS = Symbol('editing picks');

const Reasons = {
    mouse: 'BMaps manages the mouse and editing modes of the 3D view',
    undo: 'it could revert changes BMaps made to its own molecules',
    wizard: 'wizards and desktop panels are not available here',
    size: 'BMaps manages the size of the 3D view',
    internal: 'it is an internal key-binding command',
    destructive: 'it would destroy BMaps\' 3D scene',
    io: 'file, network and filesystem access is not allowed here; use BMaps\' own import and export',
};

const view = { cls: PolicyClass.view };
const deny = (reason) => ({ cls: PolicyClass.deny, reason });
const io = { cls: PolicyClass.io, reason: Reasons.io };
const P = (text) => text.split(' ');
const utilColor = { cls: PolicyClass.appearance, params: P('selection'), sel: { selection: 'all' } };

/**
 * keyword -> policy. Every keyword of PyMOL 3.2's table that the wrapper's restricted mode
 * lets through must be listed here; anything else is refused ("not available"), so a newer
 * PyMOL cannot widen the policy silently. Keywords the wrapper already refuses (Python, the
 * LITERAL expression commands, SECURE, the I/O deny-list, movie scripting, reinitialize, quit)
 * need no entry.
 *
 * Fields: `params` positional parameter names (binding); `legacy` PyMOL LEGACY parsing;
 * `sel` atom-selection parameters -> default when absent; `names` object-name-pattern
 * parameters -> default (null = not checked when absent); `newNames` parameters that name a
 * new object / selection (the `_bm_` prefix is reserved); `picks` acts on the editing picks;
 * `why` wording of a structural refusal; `check` special-case function.
 */
export const CommandPolicy = {
    // ---- view, render, query, movie, help: no effect on BMaps' molecules
    ...Object.fromEntries([
        'api', 'backward', 'bg_color', 'capture', 'center', 'centerofmass', 'check', 'clip', 'cls',
        'count_atoms', 'count_discrete', 'count_frames', 'count_states', 'deselect', 'draw', 'ending',
        'feedback', 'forward', 'frame', 'get', 'get_angle', 'get_area', 'get_bond', 'get_chains',
        'get_clip', 'get_dihedral', 'get_distance', 'get_extent', 'get_position', 'get_property',
        'get_property_list', 'get_renderer', 'get_symmetry', 'get_title', 'get_type', 'get_version',
        'get_view', 'get_viewport', 'help', 'help_setting', 'id_atom', 'identify', 'index', 'indicate',
        'intra_rms', 'intra_rms_cur', 'madd', 'mclear', 'mcopy', 'mdelete', 'mdump', 'mem',
        'meter_reset', 'middle', 'minsert', 'mmatrix', 'mmove', 'move', 'mplay', 'mset', 'mstop',
        'mtoggle', 'mview', 'order', 'orient', 'origin', 'overlap', 'phi_psi', 'ray', 'rebuild',
        'recolor', 'reference', 'refresh', 'rewind', 'rms', 'rms_cur', 'rock', 'scene_order',
        'sculpt_purge', 'set_color', 'set_view', 'space', 'splash', 'stereo', 'turn', 'unpick',
        'view', 'zoom', 'movie.nutate', 'movie.pause', 'movie.rock', 'movie.roll', 'movie.screw',
        'movie.sweep', 'movie.tdroll', 'movie.zoom',
    ].map((k) => [k, view])),
    // ... but sizes that would exhaust the wasm instance's memory are refused (an OOM aborts
    // the instance, and the automatic fall-back covers only a load failure)
    ray: { cls: PolicyClass.view, params: P('width height antialias quiet async'), check: checkImageSize },
    draw: { cls: PolicyClass.view, params: P('width height antialias quiet'), check: checkImageSize },
    mset: { cls: PolicyClass.view, params: P('specification frame'), check: checkMovieLength },
    get_sasa_relative: {
        cls: PolicyClass.view, params: P('selection state vis var quiet outfile'), fileParam: 'outfile',
    },
    diagnostics: { cls: PolicyClass.view, params: P('filename compact quiet'), fileParam: 'filename' },

    // ---- appearance: allowed; on BMaps' molecules it is a user overlay
    as: { cls: PolicyClass.appearance, params: P('representation selection'), sel: { selection: 'all' } },
    show: { cls: PolicyClass.appearance, params: P('representation selection'), sel: { selection: 'all' } },
    hide: { cls: PolicyClass.appearance, params: P('representation selection'), sel: { selection: 'all' } },
    toggle: { cls: PolicyClass.appearance, params: P('representation selection'), sel: { selection: 'all' } },
    color: { cls: PolicyClass.appearance, params: P('color selection quiet flags'), sel: { selection: 'all' } },
    color_deep: { cls: PolicyClass.appearance, params: P('color name quiet'), names: { name: 'all' } },
    spectrum: {
        cls: PolicyClass.appearance,
        params: P('expression palette selection minimum maximum byres quiet'),
        sel: { selection: 'all' },
    },
    cartoon: { cls: PolicyClass.appearance, params: P('type selection'), sel: { selection: 'all' } },
    set: {
        cls: PolicyClass.appearance, legacy: true, params: P('name value selection state updates log quiet'), check: checkSet,
    },
    unset: { cls: PolicyClass.appearance, params: P('name selection state updates log quiet'), check: checkSet },
    set_bond: {
        cls: PolicyClass.appearance,
        params: P('name value selection1 selection2 state updates log quiet'),
        sel: { selection1: null, selection2: null },
    },
    unset_bond: {
        cls: PolicyClass.appearance,
        params: P('name selection1 selection2 state updates log quiet'),
        sel: { selection1: null, selection2: null },
    },
    unset_deep: { cls: PolicyClass.appearance, params: P('settings object updates quiet'), names: { object: 'all' } },
    enable: { cls: PolicyClass.appearance, params: P('name parents'), names: { name: 'all' } },
    disable: { cls: PolicyClass.appearance, params: P('name'), names: { name: 'all' } },
    mask: { cls: PolicyClass.appearance, params: P('selection quiet'), sel: { selection: 'all' } },
    unmask: { cls: PolicyClass.appearance, params: P('selection quiet'), sel: { selection: 'all' } },
    protect: { cls: PolicyClass.appearance, params: P('selection quiet'), sel: { selection: 'all' } },
    deprotect: { cls: PolicyClass.appearance, params: P('selection quiet'), sel: { selection: 'all' } },
    dss: { cls: PolicyClass.appearance, params: P('selection state context preserve quiet'), sel: { selection: 'all' } },
    desaturate: { cls: PolicyClass.appearance, params: P('selection a quiet'), sel: { selection: 'all' } },
    flag: {
        cls: PolicyClass.appearance, legacy: true, params: P('flag selection action quiet'), sel: { selection: null },
    },
    set_title: { cls: PolicyClass.appearance, params: P('object state text'), names: { object: null } },
    set_property: {
        cls: PolicyClass.appearance, params: P('name value object state proptype quiet'), names: { object: 'all' },
    },
    set_atom_property: {
        cls: PolicyClass.appearance, params: P('name value selection state proptype quiet'), sel: { selection: 'all' },
    },
    set_symmetry: { cls: PolicyClass.appearance, params: P('selection'), sel: { selection: null } },
    assign_stereo: { cls: PolicyClass.appearance, params: P('selection state method quiet prop'), sel: { selection: 'all' } },
    isolevel: { cls: PolicyClass.appearance, params: P('name level state query quiet'), names: { name: null } },
    ramp_update: { cls: PolicyClass.appearance, params: P('name range color quiet'), names: { name: null } },
    volume_color: { cls: PolicyClass.appearance, params: P('name ramp state quiet'), names: { name: null } },
    scene: {
        cls: PolicyClass.appearance,
        params: P('key action message view color active rep frame animate new_key hand quiet sele'),
        check: checkScene,
    },
    'util.cbag': utilColor,
    'util.cbac': utilColor,
    'util.cbay': utilColor,
    'util.cbas': utilColor,
    'util.cbap': utilColor,
    'util.cbak': utilColor,
    'util.cbaw': utilColor,
    'util.cbab': utilColor,
    'util.cbao': utilColor,
    'util.cbam': utilColor,
    'util.cbc': utilColor,
    'util.chainbow': utilColor,
    'util.cnc': utilColor,
    'util.ss': utilColor,
    'util.rainbow': utilColor,

    // ---- user objects: allowed; the `_bm_` name prefix is reserved
    select: {
        cls: PolicyClass.user, legacy: true, params: P('name selection enable quiet merge state domain'), check: checkSelect,
    },
    distance: {
        cls: PolicyClass.user, legacy: true, params: P('name selection1 selection2 cutoff mode zoom width length gap label quiet reset state state1 state2'), newNames: ['name'],
    },
    angle: { cls: PolicyClass.user, params: P('name selection1 selection2 selection3'), newNames: ['name'] },
    dihedral: { cls: PolicyClass.user, params: P('name selection1 selection2 selection3 selection4'), newNames: ['name'] },
    pi_interactions: { cls: PolicyClass.user, params: P('name selection1 selection2'), newNames: ['name'] },
    pseudoatom: { cls: PolicyClass.user, params: P('object selection name'), newNames: ['object'] },
    fab: { cls: PolicyClass.user, params: P('input name'), newNames: ['name'] },
    fnab: { cls: PolicyClass.user, params: P('input name'), newNames: ['name'] },
    fragment: { cls: PolicyClass.user, params: P('name object'), newNames: ['name', 'object'] },
    map_new: {
        cls: PolicyClass.user, params: P('name type grid selection'), newNames: ['name'], check: checkMapGrid,
    },
    isomesh: {
        cls: PolicyClass.user, legacy: true, params: P('name map level selection buffer state carve source_state quiet'), newNames: ['name'],
    },
    isosurface: {
        cls: PolicyClass.user, legacy: true, params: P('name map level selection buffer state carve source_state side mode quiet'), newNames: ['name'],
    },
    isodot: {
        cls: PolicyClass.user, legacy: true, params: P('name map level selection buffer state carve source_state quiet'), newNames: ['name'],
    },
    volume: { cls: PolicyClass.user, params: P('name map ramp selection'), newNames: ['name'] },
    gradient: { cls: PolicyClass.user, params: P('name map'), newNames: ['name'] },
    slice_new: { cls: PolicyClass.user, params: P('name map'), newNames: ['name'] },
    ramp_new: { cls: PolicyClass.user, params: P('name map_name'), newNames: ['name'] },
    volume_ramp_new: { cls: PolicyClass.user, params: P('name ramp'), newNames: ['name'] },
    curve_new: { cls: PolicyClass.user, params: P('name curve_type'), newNames: ['name'] },
    callout: { cls: PolicyClass.user, params: P('name label'), newNames: ['name'] },
    pop: { cls: PolicyClass.user, params: P('name source'), newNames: ['name'] },

    // ---- structural: refused when the target is one of BMaps' molecules
    delete: {
        cls: PolicyClass.structural, params: P('name'), names: { name: null }, why: 'would delete',
    },
    delete_states: { cls: PolicyClass.structural, params: P('name states'), names: { name: null } },
    remove: {
        cls: PolicyClass.structural, params: P('selection quiet'), sel: { selection: null }, why: 'would remove atoms of',
    },
    remove_picked: { cls: PolicyClass.structural, picks: true },
    create: {
        cls: PolicyClass.structural,
        legacy: true,
        params: P('name selection source_state target_state discrete zoom quiet singletons extract copy_properties'),
        sel: { selection: null },
        newNames: ['name'],
        why: 'would duplicate the atom ids of',
    },
    extract: {
        cls: PolicyClass.structural, params: P('name selection'), sel: { selection: null }, newNames: ['name'], why: 'would take atoms out of',
    },
    copy: {
        cls: PolicyClass.structural, legacy: true, params: P('target source zoom'), names: { source: null }, newNames: ['target'], why: 'would duplicate the atom ids of',
    },
    copy_to: {
        cls: PolicyClass.structural, params: P('name selection rename zoom quiet'), names: { name: null }, sel: { selection: null },
    },
    split_chains: {
        cls: PolicyClass.structural, params: P('selection prefix group quiet'), sel: { selection: 'all' }, why: 'would duplicate the atom ids of',
    },
    split_states: { cls: PolicyClass.structural, params: P('object first last prefix'), names: { object: null } },
    join_states: {
        cls: PolicyClass.structural, params: P('name selection mode zoom quiet'), sel: { selection: 'all' }, newNames: ['name'],
    },
    symexp: {
        cls: PolicyClass.structural, legacy: true, params: P('prefix object selection cutoff segi quiet'), names: { object: null }, newNames: ['prefix'],
    },
    symmetry_copy: { cls: PolicyClass.structural, params: P('source_name target_name'), names: { target_name: null } },
    morph: {
        cls: PolicyClass.structural, params: P('name sele1 sele2'), sel: { sele1: null, sele2: null }, newNames: ['name'],
    },
    h_add: {
        cls: PolicyClass.structural, params: P('selection quiet state legacy'), sel: { selection: 'all' }, why: 'would add hydrogens to',
    },
    h_fill: { cls: PolicyClass.structural, picks: true },
    h_fix: { cls: PolicyClass.structural, params: P('selection quiet'), sel: { selection: PICKS } },
    bond: { cls: PolicyClass.structural, params: P('atom1 atom2 order'), sel: { atom1: PICKS, atom2: PICKS } },
    unbond: { cls: PolicyClass.structural, params: P('atom1 atom2 quiet'), sel: { atom1: PICKS, atom2: PICKS } },
    valence: { cls: PolicyClass.structural, params: P('order selection1 selection2'), sel: { selection1: PICKS, selection2: PICKS } },
    fix_chemistry: { cls: PolicyClass.structural, params: P('selection1 selection2 invalidate quiet'), sel: { selection1: 'all', selection2: 'all' } },
    rebond: { cls: PolicyClass.structural, params: P('oname state'), names: { oname: null } },
    cycle_valence: { cls: PolicyClass.structural, picks: true },
    invert: { cls: PolicyClass.structural, picks: true },
    torsion: { cls: PolicyClass.structural, picks: true },
    attach: { cls: PolicyClass.structural, picks: true },
    replace: { cls: PolicyClass.structural, picks: true },
    editing_ring: { cls: PolicyClass.structural, picks: true },
    edit: {
        cls: PolicyClass.structural,
        params: P('selection1 selection2 selection3 selection4 pkresi pkbond quiet'),
        sel: {
            selection1: null, selection2: null, selection3: null, selection4: null,
        },
        why: 'would start editing',
    },
    drag: { cls: PolicyClass.structural, params: P('selection wizard edit quiet mode'), sel: { selection: PICKS } },
    fuse: { cls: PolicyClass.structural, params: P('selection1 selection2 mode recolor move'), sel: { selection1: PICKS, selection2: PICKS } },
    set_dihedral: {
        cls: PolicyClass.structural,
        params: P('atom1 atom2 atom3 atom4 angle state quiet'),
        sel: {
            atom1: null, atom2: null, atom3: null, atom4: null,
        },
    },
    set_geometry: { cls: PolicyClass.structural, params: P('selection geometry valence'), sel: { selection: null } },
    clean: {
        cls: PolicyClass.structural, params: P('selection'), sel: { selection: null }, why: 'would move atoms of',
    },
    sculpt_activate: { cls: PolicyClass.structural, params: P('object state match_state match_by_segment'), names: { object: null } },
    sculpt_deactivate: { cls: PolicyClass.structural, params: P('object'), names: { object: null } },
    sculpt_iterate: { cls: PolicyClass.structural, params: P('object state cycles'), names: { object: null } },
    translate: {
        cls: PolicyClass.structural,
        params: P('vector selection state camera object object_mode'),
        sel: { selection: 'all' },
        names: { object: null },
        why: 'would move',
    },
    rotate: {
        cls: PolicyClass.structural,
        params: P('axis angle selection state camera object origin object_mode'),
        sel: { selection: 'all' },
        names: { object: null },
        why: 'would move',
    },
    matrix_reset: { cls: PolicyClass.structural, params: P('name state mode'), names: { name: null } },
    matrix_copy: { cls: PolicyClass.structural, params: P('source_name target_name'), names: { target_name: null } },
    reset: { cls: PolicyClass.structural, params: P('object'), names: { object: null } },
    look_at: { cls: PolicyClass.structural, params: P('target_obj mobile_obj'), names: { mobile_obj: null } },
    update: {
        cls: PolicyClass.structural, params: P('target source'), sel: { target: null }, why: 'would change the coordinates of',
    },
    align: {
        cls: PolicyClass.structural,
        params: P('mobile target cutoff cycles gap extend max_gap object matrix mobile_state target_state quiet max_skip transform reset'),
        sel: { mobile: null },
        noTransformParam: 'transform',
        why: 'would move',
    },
    super: {
        cls: PolicyClass.structural,
        params: P('mobile target cutoff cycles gap extend max_gap object matrix mobile_state target_state quiet max_skip transform reset'),
        sel: { mobile: null },
        noTransformParam: 'transform',
        why: 'would move',
    },
    cealign: {
        cls: PolicyClass.structural,
        params: P('target mobile target_state mobile_state quiet guide d0 d1 window gap_max transform object'),
        sel: { mobile: null },
        noTransformParam: 'transform',
        why: 'would move',
    },
    usalign: {
        cls: PolicyClass.structural,
        params: P('mobile target mobile_state target_state quiet transform object fast'),
        sel: { mobile: null },
        noTransformParam: 'transform',
        why: 'would move',
    },
    fit: {
        cls: PolicyClass.structural, params: P('mobile target'), sel: { mobile: null }, why: 'would move',
    },
    pair_fit: { cls: PolicyClass.structural, allArgsAreSelections: true, why: 'would move' },
    intra_fit: {
        cls: PolicyClass.structural, params: P('selection state quiet mix'), sel: { selection: null }, why: 'would move',
    },
    alignto: {
        cls: PolicyClass.structural, params: P('target method selection'), sel: { selection: 'all' }, why: 'would move',
    },
    extra_fit: {
        cls: PolicyClass.structural, params: P('selection reference'), sel: { selection: 'all' }, why: 'would move',
    },
    vdw_fit: {
        cls: PolicyClass.structural, params: P('selection1 selection2'), sel: { selection1: null, selection2: null }, why: 'would move',
    },
    smooth: {
        cls: PolicyClass.structural, params: P('selection'), sel: { selection: 'all' }, why: 'would move atoms of',
    },
    spheroid: { cls: PolicyClass.structural, params: P('object average'), names: { object: 'all' } },
    sort: { cls: PolicyClass.structural, params: P('object'), names: { object: 'all' } },
    rename: {
        cls: PolicyClass.structural, params: P('selection force quiet'), sel: { selection: 'all' }, why: 'would rename atoms of',
    },
    uniquify: {
        cls: PolicyClass.structural, params: P('identifier selection'), sel: { selection: null }, why: 'would rename atoms of',
    },
    alphatoall: { cls: PolicyClass.structural, params: P('selection properties operator quiet'), sel: { selection: 'polymer' } },
    mse2met: { cls: PolicyClass.structural, params: P('selection quiet'), sel: { selection: 'all' } },
    protonate: {
        cls: PolicyClass.structural, params: P('selection'), sel: { selection: 'all' }, why: 'would add hydrogens to',
    },
    pbc_wrap: { cls: PolicyClass.structural, params: P('oname center'), names: { oname: null } },
    pbc_unwrap: { cls: PolicyClass.structural, params: P('oname bymol'), names: { oname: null } },
    map_set: { cls: PolicyClass.structural, params: P('name operator operands'), names: { name: null } },
    map_set_border: { cls: PolicyClass.structural, params: P('name level state'), names: { name: null } },
    map_double: { cls: PolicyClass.structural, params: P('name state'), names: { name: null } },
    map_halve: { cls: PolicyClass.structural, params: P('name state smooth'), names: { name: null } },
    map_trim: { cls: PolicyClass.structural, params: P('name selection'), names: { name: null } },
    group: {
        cls: PolicyClass.structural, params: P('name members action quiet'), names: { members: null }, newNames: ['name'], why: 'would regroup',
    },
    ungroup: {
        cls: PolicyClass.structural, params: P('members quiet'), names: { members: null }, why: 'would regroup',
    },
    set_name: {
        cls: PolicyClass.structural, params: P('old_name new_name'), names: { old_name: null }, newNames: ['new_name'], why: 'would rename',
    },

    // ---- always refused
    mouse: deny(Reasons.mouse),
    edit_mode: deny(Reasons.mouse),
    button: deny(Reasons.mouse),
    config_mouse: deny(Reasons.mouse),
    undo: deny(Reasons.undo),
    redo: deny(Reasons.undo),
    wizard: deny(Reasons.wizard),
    replace_wizard: deny(Reasons.wizard),
    refresh_wizard: deny(Reasons.wizard),
    accept: deny(Reasons.wizard),
    decline: deny(Reasons.wizard),
    volume_panel: deny(Reasons.wizard),
    viewport: deny(Reasons.size),
    full_screen: deny(Reasons.size),
    window: deny(Reasons.size),
    _alt: deny(Reasons.internal),
    _ctrl: deny(Reasons.internal),
    _ctsh: deny(Reasons.internal),
    _special: deny(Reasons.internal),
    _quit: deny(Reasons.destructive),
    dump: io,
    dir: io,
    multisave: io,
    multifilesave: io,
    resume: io,
    focal_blur: io,
    'movie.load': io,
    'movie.produce': io,
};

/** `set` / `unset`: global when no selection is given; BMaps' own globals are protected. */
function checkSet(args, ctx) {
    const selection = args.selection == null ? '' : stripQuotes(args.selection);
    if (selection === '') {
        if (isProtectedSetting(args.name)) {
            return {
                refuse: `'${ctx.keyword} ${stripQuotes(args.name || '')}' - BMaps relies on this global setting; set it for a selection or object instead`,
            };
        }
        return {}; // a render setting (ray_trace_mode, ambient ...): no overlay
    }
    return { sel: [selection] };
}

// ---- sizes: what an allowed command may allocate in the wasm instance
const MaxMovieFrames = 10000;
const MinMapGridSpacing = 0.2; // Å; PyMOL's own default is 0.5-1.0

/** A numeric argument, or null when it cannot be read - PyMOL then reports it itself. */
function numberOf(value) {
    if (value == null) return null;
    const n = Number(stripQuotes(value));
    return Number.isFinite(n) ? n : null;
}

/** `ray` / `draw` width and height: the ray-traced image export's own ceiling, per side. */
function checkImageSize(args, ctx) {
    const width = numberOf(args.width);
    const height = numberOf(args.height);
    const tooBig = (n) => n != null && n > RayMaxDimension;
    if (tooBig(width) || tooBig(height)) {
        return {
            refuse: `'${ctx.keyword} ${stripQuotes(args.width || '')}, ${stripQuotes(args.height || '')}' - an image over ${RayMaxDimension} px on a side would exhaust the viewer's memory (the image export has the same limit)`,
        };
    }
    return null;
}

/**
 * Frames an `mset` specification expands to: "1" one frame, "1 x30" thirty of state 1,
 * "1 -100" states 1 to 100, and combinations ("1 x10 2 -20"). Null when the text is not
 * something this reads, which PyMOL then reports itself.
 * @param {string} specification
 * @returns {number|null}
 */
export function movieFrameCount(specification) {
    const tokens = stripQuotes(specification == null ? '' : specification).split(/\s+/).filter(Boolean);
    let count = 0;
    let last = null;
    for (const token of tokens) {
        const repeat = /^x(\d+)$/i.exec(token);
        const range = /^-(\d+)$/.exec(token);
        const single = /^(\d+)$/.exec(token);
        if (repeat && last != null) {
            count += Number(repeat[1]) - 1;
        } else if (range && last != null) {
            count += Math.abs(Number(range[1]) - last);
            last = Number(range[1]);
        } else if (single) {
            count += 1;
            last = Number(single[1]);
        } else {
            return null;
        }
    }
    return count;
}

function checkMovieLength(args, ctx) {
    const frames = movieFrameCount(args.specification);
    if (frames != null && frames > MaxMovieFrames) {
        return {
            refuse: `'${ctx.keyword} ${stripQuotes(args.specification)}' - ${frames} frames would exhaust the viewer's memory (at most ${MaxMovieFrames})`,
        };
    }
    return null;
}

function checkMapGrid(args, ctx) {
    const grid = numberOf(args.grid);
    if (grid != null && grid > 0 && grid < MinMapGridSpacing) {
        return {
            refuse: `'${ctx.keyword}' with a ${grid} Å grid would exhaust the viewer's memory (finest ${MinMapGridSpacing} Å)`,
        };
    }
    return null;
}

/** `select name, selection` or `select selection` (the selection is then called `sele`). */
function checkSelect(args) {
    const hasSelection = args.selection != null && stripQuotes(args.selection) !== '';
    return { newNames: [hasSelection ? args.name : 'sele'] };
}

const SceneStoreActions = ['store', 'update', 'append', 'insert_after', 'insert_before', 'delete', 'clear', 'rename', 'order', 'sort'];

/** `scene` recall restores representations and colors of every object it recorded. */
function checkScene(args) {
    const action = stripQuotes(args.action || 'recall').toLowerCase();
    if (SceneStoreActions.includes(action)) return {};
    return { allManaged: true };
}

// ----------------------------------------------------------------------------------------------
// The controller
// ----------------------------------------------------------------------------------------------

const BlockKeywords = ['python', 'skip', 'embed'];
const MaxNamesInMessage = 3;

function outLine(kind, text) { return { kind, text }; }

function listNames(names) {
    const shown = names.slice(0, MaxNamesInMessage).join(', ');
    return names.length > MaxNamesInMessage ? `${shown} ...` : shown;
}

const Decisions = ['allow', 'refuse', 'open'];

/**
 * Plain JS value of a classify() result (plain objects; Python None may be undefined). The
 * bridge always sends a decision and a class; anything else (an older payload, a proxy that
 * lost a field) fails CLOSED: no decision is a refusal, no class is 'unclassified' (refused).
 */
function normalizeClassification(c) {
    const commands = Array.isArray(c && c.commands) ? c.commands : [];
    return {
        decision: c && Decisions.includes(c.decision) ? c.decision : 'refuse',
        reason: (c && c.reason) || null,
        commands: commands.map(([word, keyword, cls]) => ({
            word: word == null ? '' : String(word),
            keyword: keyword == null ? null : String(keyword),
            cls: cls == null ? 'unclassified' : String(cls),
        })),
    };
}

/** One-line refusal wording for the wrapper's own classes. */
function bridgeRefusal(cmd) {
    const name = cmd.keyword || cmd.word;
    switch (cmd.cls) {
        case 'python':
            return `'${cmd.word}' - Python code is not allowed here (restricted mode)`;
        case 'expression':
            return `'${name}' evaluates Python expressions; not allowed here (restricted mode)`;
        case 'secure':
            return `'${name}' runs scripts or writes files; not allowed here (restricted mode)`;
        case 'io':
            return `'${name}' - ${Reasons.io}`;
        case 'movie':
            return `'${name}' - movie scripting is not allowed here (restricted mode)`;
        case 'embed':
        case 'flow':
            return `'${name}' - script blocks are not allowed here (restricted mode)`;
        case 'destructive':
            return `'${name}' - ${Reasons.destructive}`;
        case 'denied':
            return `'${name}' is disabled in BMaps`;
        case 'unclassified':
        case 'allowed': // a refuse decision without a class of its own
            return `'${name}' could not be classified; not run`;
        default:
            return `'${name}' is not allowed here (${cmd.cls})`;
    }
}

/**
 * The command line of one VisualizerPyMOL. Reads the visualizer's `viewer`, `scene` and
 * `whenReady` on every call, so it follows InitViewer() / dispose().
 */
export class PyMOLCommands {
    /** @param {{visualizer: object}} options the VisualizerPyMOL */
    constructor({ visualizer }) {
        this.visualizer = visualizer;
        /** Managed object names the user changed the appearance of (the "user overlay"). */
        this.overlayObjects = new Set();
        this.watchedReady = null;
        this.unrestrictedRequested = false;
        this.lastAnnounced = null;
        this.watchReady();
    }

    get viewer() { return this.visualizer.viewer || null; }

    // ---------------------------------------------------------------- state

    /** @returns {'ready'|'loading'|'failed'|'unavailable'} */
    status() {
        const vis = this.visualizer;
        if (vis.viewer) return 'ready';
        if (vis.scene && vis.scene.failed) return 'failed';
        if (!vis.scene) return 'unavailable';
        return 'loading';
    }

    /**
     * What the command panel shows. `overlay`: the user changed the appearance of BMaps'
     * molecules and they have not been redrawn since.
     */
    state() {
        this.watchReady();
        const status = this.status();
        const messages = {
            ready: null,
            loading: 'The 3D viewer is still loading.',
            failed: 'The 3D viewer failed to load; viewer commands are unavailable.',
            unavailable: 'The 3D viewer is not running.',
        };
        return {
            status,
            message: messages[status],
            overlay: status === 'ready' ? this.hasOverlay() : false,
            mode: this.executionMode(),
            attribution: Attribution,
            trademark: TrademarkNotice,
            restrictions: RestrictionSummary,
        };
    }

    /** Announce ready / failed once the payload settles (the panel may be open already). */
    watchReady() {
        const ready = this.visualizer.whenReady;
        if (!ready || ready === this.watchedReady || typeof ready.then !== 'function') return;
        this.watchedReady = ready;
        ready.then(() => this.announce(), () => this.announce());
    }

    announce() {
        const state = this.state();
        const key = `${state.status}|${state.overlay}`;
        if (key === this.lastAnnounced) return;
        this.lastAnnounced = key;
        EventBroker.publish(NativeCommandStateEvent, state);
    }

    /** O6 hook: always 'restricted' while UnrestrictedModeEnabled is false. */
    executionMode() {
        return UnrestrictedModeEnabled && this.unrestrictedRequested ? 'full' : 'restricted';
    }

    /** O6 hook, inert: returns whether unrestricted mode is now in effect (never, today). */
    setUnrestrictedRequested(requested) {
        this.unrestrictedRequested = !!requested;
        return this.executionMode() === 'full';
    }

    hasOverlay() {
        if (this.overlayObjects.size === 0 || !this.viewer) return this.overlayObjects.size > 0;
        // Reset()/redrawScene() replaces BMaps' objects with new ones under new names (the
        // counter never repeats), so an overlay ends when its objects are gone.
        const present = new Set(this.objectNames());
        for (const name of [...this.overlayObjects]) {
            if (!present.has(name)) this.overlayObjects.delete(name);
        }
        return this.overlayObjects.size > 0;
    }

    // ---------------------------------------------------------------- PyMOL queries

    /** Every object and selection name, hidden `_` names included. */
    allNames() {
        const names = this.viewer.cmd.get_names('all');
        return Array.isArray(names) ? names.map(String) : [];
    }

    objectNames() {
        try {
            const names = this.viewer.cmd.get_names('objects');
            return Array.isArray(names) ? names.map(String) : [];
        } catch (ex) {
            return [];
        }
    }

    managedObjectNames() {
        return this.objectNames().filter(isManagedName);
    }

    /**
     * Molecular objects an atom selection reaches. Throws when PyMOL cannot evaluate it.
     * @param {string} selection
     * @returns {string[]}
     */
    objectsInSelection(selection) {
        const objects = this.viewer.cmd.get_object_list(selection);
        if (!Array.isArray(objects)) throw new Error(`invalid selection "${selection}"`);
        return objects.map(String);
    }

    // ---------------------------------------------------------------- policy

    classify(text) {
        return normalizeClassification(this.viewer.classify(text));
    }

    /**
     * Static checks of a whole paste, before anything runs: blocks, line continuations,
     * the wrapper's classes, keywords BMaps never allows.
     * @returns {{commands: object[], refusals: string[]}}
     */
    planText(text) {
        const lines = String(text).replace(/\r\n?/g, '\n').split('\n');
        const commands = [];
        const refusals = [];
        for (let i = 0; i < lines.length; i++) {
            const lineNo = i + 1;
            if (!lines[i].trim()) continue;
            if (/\\\s*$/.test(lines[i])) {
                refusals.push(`line ${lineNo} ends with '\\' (line continuation is not supported here)`);
                continue;
            }
            const chunks = splitCommands(lines[i]);
            for (let k = 0; k < chunks.length; k++) {
                const chunk = chunks[k];
                // NB a '#' command does NOT comment out the rest of the line: PyMOL's parser
                // skips it and runs what follows the next ';'. So every chunk is checked.
                if (chunk.startsWith('#')) continue;
                const c = this.classify(chunk);
                if (c.commands.length === 0) {
                    // blank is fine; text the classifier saw nothing in (`,foo`) is not run
                    // silently either
                    if (!chunk.trim()) continue;
                    refusals.push(`line ${lineNo}: '${chunk}' could not be classified; not run`);
                    continue;
                }
                if (c.commands.length !== 1) {
                    refusals.push(`line ${lineNo}: '${chunk}' could not be split into single commands safely`);
                    break;
                }
                const cmd = c.commands[0];
                if (k === 0 && BlockKeywords.includes(cmd.keyword)) {
                    const block = this.findBlockEnd(lines, i, chunk, cmd.keyword);
                    refusals.push(block.message);
                    i = block.endIndex;
                    break;
                }
                if (/\\\s*$/.test(chunk)) {
                    refusals.push(`line ${lineNo}: '${chunk}' ends with '\\' (line continuation is not supported here)`);
                    continue;
                }
                const planned = {
                    line: lineNo, chunk, word: cmd.word, keyword: cmd.keyword, cls: cmd.cls,
                };
                if (cmd.cls === 'ambiguous') {
                    planned.policy = null; // PyMOL reports the candidates itself and runs nothing
                } else if (cmd.cls !== 'allowed' || c.decision === 'refuse') {
                    refusals.push(bridgeRefusal(cmd));
                    continue;
                } else {
                    planned.policy = CommandPolicy[cmd.keyword] || null;
                    if (!planned.policy) {
                        refusals.push(`'${cmd.keyword}' is not available in BMaps' viewer command line`);
                        continue;
                    }
                    const { cls } = planned.policy;
                    if (cls === PolicyClass.deny || cls === PolicyClass.io) {
                        refusals.push(`'${cmd.keyword}' - ${planned.policy.reason}`);
                        continue;
                    }
                }
                commands.push(planned);
            }
        }
        return { commands, refusals };
    }

    /** A `python` / `skip` / `embed` block: where it ends, and the one-line refusal. */
    findBlockEnd(lines, startIndex, chunk, keyword) {
        let sentinel = `${keyword} end`;
        try {
            const [first] = parseArgs(chunk);
            if (first && first[1]) sentinel = stripQuotes(first[1]);
        } catch (ex) { /* keep the default sentinel */ }
        const what = keyword === 'python' ? 'Python code is not allowed here (restricted mode)'
            : 'script blocks are not allowed here (restricted mode)';
        const startNo = startIndex + 1;
        if (lines[startIndex].trim() === `${keyword} end`) {
            return { endIndex: startIndex, message: `line ${startNo}: '${keyword} end' - ${what}` };
        }
        for (let j = startIndex + 1; j < lines.length; j++) {
            if (lines[j].trim() === sentinel) {
                return {
                    endIndex: j,
                    message: `lines ${startNo}-${j + 1} are a '${keyword} ... ${sentinel}' block - ${what}`,
                };
            }
        }
        return {
            endIndex: lines.length - 1,
            message: `line ${startNo} opens a '${keyword}' block that is never closed with '${sentinel}' - ${what}`,
        };
    }

    /**
     * The checks that depend on PyMOL's current state, right before one command runs.
     * @returns {{refuse?: string, overlay?: string[]}}
     */
    checkTargets(planned) {
        const { policy, keyword } = planned;
        if (!policy) return {};
        if (policy.cls === PolicyClass.view && !policy.fileParam && !policy.check) return {};
        let bound;
        try {
            const parsed = parseArgs(planned.chunk);
            if (policy.allArgsAreSelections) {
                bound = { args: {}, extra: parsed.map(([, value]) => value) };
            } else {
                bound = bindArgs(policy.params || [], parsed, { legacy: !!policy.legacy });
            }
        } catch (ex) {
            if (policy.cls === PolicyClass.appearance) return { overlayAll: true };
            return { refuse: `'${keyword}' - its arguments could not be checked (${ex.message})` };
        }
        const { args } = bound;
        if (policy.fileParam && args[policy.fileParam] && stripQuotes(args[policy.fileParam]) !== '') {
            return { refuse: `'${keyword}' with a file name - ${Reasons.io}` };
        }
        // a `check` runs for view commands too (ray / draw / mset sizes)
        const special = policy.check ? policy.check(args, { keyword }) : null;
        if (special && special.refuse) return { refuse: special.refuse };
        if (policy.cls === PolicyClass.view) return {};

        // new names: the `_bm_` prefix is reserved for BMaps' objects
        const newNames = special && special.newNames ? special.newNames
            : (policy.newNames || []).map((p) => args[p]);
        for (const name of newNames) {
            if (name != null && isManagedName(name)) {
                return { refuse: `'${keyword}' - names starting with "${ObjectPrefix}" are reserved for BMaps' own objects` };
            }
        }

        // what the command reaches
        const managedHit = new Set();
        const allManaged = !!(special && special.allManaged);
        const selections = [];
        if (special && special.sel) {
            selections.push(...special.sel);
        } else if (!special) {
            for (const [param, dflt] of Object.entries(policy.sel || {})) {
                const given = args[param] != null && stripQuotes(args[param]) !== '' ? args[param] : null;
                const value = given != null ? given : dflt;
                if (value != null) selections.push(value);
            }
            if (policy.allArgsAreSelections) selections.push(...bound.extra.filter((v) => v != null && v !== ''));
            if (policy.picks) selections.push(PICKS);
            if (policy.noTransformParam && stripQuotes(args[policy.noTransformParam] || '1') === '0') {
                selections.length = 0; // align ..., transform=0 only reports
            }
        }
        let names = null;
        const namePatterns = [];
        if (!special) {
            for (const [param, dflt] of Object.entries(policy.names || {})) {
                const given = args[param] != null && stripQuotes(args[param]) !== '' ? args[param] : null;
                const value = given != null ? given : dflt;
                if (value != null) namePatterns.push(value);
            }
        }
        try {
            if (namePatterns.length > 0 || selections.includes(PICKS)) names = this.allNames();
            for (const pattern of namePatterns) {
                namesMatching(pattern, names).filter(isManagedName)
                    .forEach((n) => managedHit.add(n));
            }
            for (const selection of selections) {
                let expression = selection;
                if (selection === PICKS) {
                    const picks = ['pk1', 'pk2', 'pk3', 'pk4'].filter((p) => names.includes(p));
                    if (picks.length === 0) continue; // PyMOL will say that nothing is picked
                    expression = picks.join(' or ');
                }
                this.objectsInSelection(expression).filter(isManagedName)
                    .forEach((n) => managedHit.add(n));
            }
        } catch (ex) {
            if (policy.cls === PolicyClass.appearance || policy.cls === PolicyClass.user) {
                return {}; // PyMOL reports the bad selection when the command runs
            }
            const detail = String((ex && ex.message) || ex).split('\n').filter(Boolean).pop() || 'error';
            return { refuse: `'${keyword}' - the target could not be checked (${detail.slice(0, 160)})` };
        }
        if (allManaged) this.managedObjectNames().forEach((n) => managedHit.add(n));

        const hit = [...managedHit];
        if (policy.cls === PolicyClass.structural && hit.length > 0) {
            const why = policy.why || 'would change';
            return {
                refuse: `'${keyword}' ${why} BMaps' own molecules (${listNames(hit)}); it is allowed only on objects you created`,
            };
        }
        if (policy.cls === PolicyClass.appearance && hit.length > 0) return { overlay: hit };
        return {};
    }

    // ---------------------------------------------------------------- execution

    /**
     * Run typed or pasted text. A paste is checked as a whole first (nothing runs if any line
     * is refused); then every command runs on its own, right after the checks that depend on
     * PyMOL's current state. As in PyMOL, an error skips the rest of its line; a refusal stops
     * everything after it.
     * @param {string} text
     * @returns {{ok: boolean, output: string[], error: string|null, refused: string|null,
     *            lines: {kind: string, text: string}[], overlay: boolean, status: string}}
     */
    run(text) {
        const status = this.status();
        const lines = [];
        const finish = (extra={}) => {
            const firstError = lines.find((l) => l.kind === 'error' || l.kind === 'refusal');
            const refusal = lines.find((l) => l.kind === 'refusal');
            const result = {
                ok: !firstError,
                output: lines.map((l) => l.text),
                error: firstError ? firstError.text : null,
                refused: refusal ? refusal.text : null,
                lines,
                overlay: status === 'ready' ? this.hasOverlay() : false,
                status,
                ...extra,
            };
            if (status === 'ready') this.announce();
            return result;
        };
        if (status !== 'ready') {
            const message = this.state().message;
            lines.push(outLine('error', message));
            return finish();
        }
        const input = String(text == null ? '' : text);
        let plan;
        try {
            plan = this.planText(input);
        } catch (ex) {
            lines.push(outLine('error', `Error: the command could not be checked (${(ex && ex.message) || ex})`));
            return finish();
        }
        if (plan.refusals.length > 0) {
            plan.refusals.forEach((r) => lines.push(outLine('refusal', `Refused: ${r}.`)));
            lines.push(outLine('info', 'Nothing was run.'));
            return finish();
        }
        const mode = this.executionMode();
        const touched = new Set();
        let skipLine = 0;
        let ran = 0;
        for (let i = 0; i < plan.commands.length; i++) {
            const planned = plan.commands[i];
            if (planned.line === skipLine) continue;
            let verdict;
            try {
                verdict = this.checkTargets(planned);
            } catch (ex) {
                verdict = { refuse: `'${planned.keyword}' could not be checked (${(ex && ex.message) || ex})` };
            }
            if (verdict.refuse) {
                lines.push(outLine('refusal', `Refused: ${verdict.refuse}.`));
                const left = plan.commands.length - i - 1;
                if (ran > 0 || left > 0) {
                    lines.push(outLine('info', `Stopped: ${ran} earlier command(s) ran; ${left} later command(s) were not run.`));
                }
                break;
            }
            let result;
            try {
                result = this.viewer.exec(planned.chunk, { mode });
            } catch (ex) {
                result = { ok: false, output: [], error: `Error: ${(ex && ex.message) || ex}` };
            }
            ran++;
            const output = Array.isArray(result.output) ? result.output : [];
            output.forEach((text2) => {
                const kind = result.refused && /refused/i.test(text2) ? 'refusal' : classifyOutputLine(text2);
                lines.push(outLine(kind, String(text2)));
            });
            if (!result.ok) {
                if (!output.some((l) => classifyOutputLine(l) === 'error')) {
                    lines.push(outLine('error', String(result.error || result.refused || 'Error: the command failed')));
                }
                skipLine = planned.line;
                continue;
            }
            if (verdict.overlay) verdict.overlay.forEach((n) => touched.add(n));
            if (verdict.overlayAll) this.managedObjectNames().forEach((n) => touched.add(n));
            if (planned.keyword === 'help' && /^\S+\s*$/.test(planned.chunk)) {
                lines.push(outLine('info', 'Commands run in restricted mode here: see "What is refused?" below the input.'));
            }
        }
        if (touched.size > 0) {
            touched.forEach((n) => this.overlayObjects.add(n));
            lines.push(outLine('info', `Changed how BMaps' molecules look (${listNames([...touched])}); "Reapply BMaps display" restores BMaps' styles.`));
        }
        return finish();
    }

    // ---------------------------------------------------------------- completion

    /**
     * Full-line completion candidates for the text before the caret. Keywords, settings,
     * representations and colors come from PyMOL's tables through the wrapper; object and
     * selection names come from here (PyMOL's own name completion does not work on a
     * `pymol2` instance). BMaps' `_bm_` names are offered only once the user types `_`.
     * @param {string} text
     * @returns {string[]}
     */
    complete(text) {
        if (!this.viewer) return [];
        const input = String(text == null ? '' : text);
        const lineStart = input.lastIndexOf('\n') + 1;
        const lineText = input.slice(lineStart);
        // the command being typed = what follows the last top-level ';'
        const segmentStart = lastCommandStart(lineText);
        const head = input.slice(0, lineStart) + lineText.slice(0, segmentStart);
        const segment = lineText.slice(segmentStart).replace(/^\s+/, '');
        const lead = lineText.slice(segmentStart, lineText.length - segment.length);
        let info = null;
        try {
            info = this.viewer.completeInfo ? this.viewer.completeInfo(segment) : null;
        } catch (ex) {
            info = null;
        }
        let candidates = [];
        let pre = '';
        let postfix = '';
        if (info && Array.isArray(info.candidates)) {
            candidates = info.candidates.map(String);
            pre = info.pre || '';
            postfix = info.postfix || '';
        } else if (this.viewer.complete) {
            try {
                return (this.viewer.complete(segment) || []).map((c) => head + lead + c);
            } catch (ex) {
                return [];
            }
        }
        const word = /[^\s,()]*$/.exec(segment)[0];
        const inArguments = /\s/.test(segment);
        const nameTypes = [null, undefined, 'selection', 'object', 'map object', 'contour', 'group object', 'names'];
        if (inArguments && word && (candidates.length === 0 || word.startsWith('_'))
            && nameTypes.includes(info ? info.type : null)) {
            // our own name completion; hidden names only when asked for explicitly
            let names = [];
            try {
                names = this.allNames();
            } catch (ex) {
                names = [];
            }
            const lower = word.toLowerCase();
            const own = names.filter((n) => n.toLowerCase().startsWith(lower)
                && (word.startsWith('_') || !n.startsWith('_')));
            if (own.length > 0) {
                const kept = candidates.filter((c) => c.toLowerCase().startsWith(lower));
                candidates = [...new Set([...kept, ...own])].sort();
                pre = segment.slice(0, segment.length - word.length);
                postfix = '';
            }
        }
        if (!word.startsWith('_')) candidates = candidates.filter((c) => !c.startsWith('_'));
        return candidates.map((c) => head + lead + pre + c + postfix);
    }
}
